/**
 * Sanitiza uma matrícula removendo TUDO que não é número,
 * mas MANTENDO zeros à esquerda.
 * 
 * @example sanitizeMatricula("0123.456-7") -> "01234567"
 * @example sanitizeMatricula("0001234") -> "0001234"
 * @example sanitizeMatricula("ABC-123") -> "123"
 */
export function sanitizeMatricula(value: string | null | undefined): string {
  if (!value) return '';
  
  // Remove TUDO que não é número, mantém zeros à esquerda
  return value.replace(/\D/g, '');
}

/**
 * Formata uma matrícula adicionando zeros à esquerda para completar o tamanho desejado.
 * 
 * @example formatMatricula("1234", 6) -> "001234"
 * @example formatMatricula("123456", 6) -> "123456"
 * @example formatMatricula("1234567", 6) -> "1234567" (não trunca)
 */
export function formatMatricula(value: string | null | undefined, padLength: number = 6): string {
  const sanitized = sanitizeMatricula(value);
  
  if (!sanitized) return '';
  
  if (sanitized.length < padLength) {
    return sanitized.padStart(padLength, '0');
  }
  
  return sanitized;
}

/**
 * Handler para input de matrícula - permite apenas números
 * Use em onChange para filtrar caracteres inválidos em tempo real
 */
export function handleMatriculaInput(value: string): string {
  return value.replace(/\D/g, '');
}

/**
 * Substitui letras por '0' (zero) e remove demais caracteres não numéricos.
 * Mantém zeros à esquerda.
 *
 * @example replaceLettersWithZero("A1B2") -> "0102"
 * @example replaceLettersWithZero("ABC-123") -> "000123"
 */
export function replaceLettersWithZero(value: string | null | undefined): string {
  if (!value) return '';
  return value
    .replace(/[A-Za-z]/g, '0')
    .replace(/[^0-9]/g, '');
}

/** Retorna true se o valor contém ao menos uma letra A-Z. */
export function containsLetters(value: string | null | undefined): boolean {
  if (!value) return false;
  return /[A-Za-z]/.test(value);
}

export const MATRICULA_TOOLTIP_TEXT =
  'Use apenas números. Letras digitadas serão substituídas por 0 (zero). Mantenha os zeros à esquerda.';

/**
 * @deprecated Use sanitizeMatricula ou formatMatricula
 * Mantido para compatibilidade, agora preserva zeros à esquerda.
 */
export function normalizeMatricula(value: string | null | undefined): string {
  return sanitizeMatricula(value);
}

/**
 * Verifica se o valor parece ser uma matrícula (não contém @)
 * e tem apenas números após limpeza
 */
export function isMatriculaFormat(value: string): boolean {
  if (!value || value.includes('@')) return false;
  const cleaned = sanitizeMatricula(value);
  return cleaned.length > 0;
}
